// Legal Holds — platform staff place and release legal holds that override all
// retention, erasure, and anonymization for a tenant, employee, record, or case.
// While a hold is ACTIVE, nothing under its scope can be modified. Every create and
// release is recorded as audit evidence. The backend list endpoint is a STUB today,
// so this page renders an informative empty-state (not an error) until holds exist.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Tag, Button, Modal, Form, Input, Select, Space, Tooltip, Empty,
  message, Descriptions, Typography,
} from 'antd';
import {
  SafetyCertificateOutlined, PlusOutlined, ReloadOutlined, EyeOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '@/app/hooks';
import { selectHasPermission } from '@/features/auth/authSlice';
import {
  getLegalHolds, createLegalHold, releaseLegalHold, executeDocumentHold, GOVERNANCE_SERVICES,
  type LegalHold, type LegalHoldInput, type DocumentHoldResult,
} from '@/services/dataGovernanceService';
import {
  DryRunBanner, LegalHoldBadge, ConfirmDangerDialog, ComplianceTimeline,
  type TimelineEvent,
} from '@/components/compliance';

const { Title, Text, Paragraph } = Typography;

const SUBJECT_TYPES: LegalHold['subject_type'][] = ['TENANT', 'EMPLOYEE', 'RECORD', 'CASE'];
const short = (id?: string | null) => (id ? id.slice(0, 8) : '—');
const subjectColor = (t: LegalHold['subject_type']) =>
  ({ TENANT: 'blue', EMPLOYEE: 'geekblue', RECORD: 'purple', CASE: 'magenta' }[t]);

const LegalHoldsPage: React.FC = () => {
  const canView = useAppSelector(selectHasPermission('compliance:legal_hold:view'));
  const canCreate = useAppSelector(selectHasPermission('compliance:legal_hold:create'));
  const canRelease = useAppSelector(selectHasPermission('compliance:legal_hold:release'));

  const [holds, setHolds] = useState<LegalHold[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters (client-side over the loaded list).
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'RELEASED' | 'all'>('all');
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<string | undefined>(undefined);

  // Create dialog.
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<LegalHoldInput>();

  // Detail view.
  const [detail, setDetail] = useState<LegalHold | null>(null);

  // Release confirmation.
  const [releaseTarget, setReleaseTarget] = useState<LegalHold | null>(null);
  const [releasing, setReleasing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getLegalHolds();
      setHolds(data);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load legal holds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) load();
  }, [canView]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return holds.filter((h) => {
      if (statusFilter !== 'all' && h.status !== statusFilter) return false;
      if (scopeFilter && !h.scope_services.includes(scopeFilter as any)) return false;
      if (q) {
        const hay = `${h.tenant_id} ${h.subject_id} ${h.employee_id ?? ''} ${h.reason}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [holds, statusFilter, search, scopeFilter]);

  const submitCreate = async () => {
    let values: LegalHoldInput;
    try {
      values = await form.validateFields();
    } catch {
      return; // validation errors shown inline
    }
    setCreating(true);
    try {
      await createLegalHold({ ...values, scope_services: (values.scope_services || []) as any });
      message.success('Legal hold placed');
      setCreateOpen(false);
      form.resetFields();
      await load();
    } catch (err: any) {
      // STUB rejects — surface the message and keep the modal open + usable.
      message.error(err?.response?.data?.message || err?.message || 'Failed to place legal hold');
    } finally {
      setCreating(false);
    }
  };

  const confirmRelease = async (reason: string) => {
    if (!releaseTarget) return;
    setReleasing(true);
    try {
      await releaseLegalHold(releaseTarget.id, reason);
      message.success('Legal hold released');
      setReleaseTarget(null);
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Failed to release legal hold');
    } finally {
      setReleasing(false);
    }
  };

  const detailEvents = (h: LegalHold): TimelineEvent[] => {
    const events: TimelineEvent[] = [
      { ts: h.created_at, label: 'Legal hold created', actor: h.created_by, note: h.reason, color: 'red' },
    ];
    if (h.status === 'RELEASED' && h.released_at) {
      events.unshift({
        ts: h.released_at, label: 'Legal hold released', actor: h.released_by,
        note: h.release_reason || undefined, color: 'green',
      });
    }
    return events;
  };

  const columns = [
    {
      title: 'Subject',
      key: 'subject',
      render: (_: unknown, h: LegalHold) => (
        <Space direction="vertical" size={0}>
          <Tag color={subjectColor(h.subject_type)}>{h.subject_type}</Tag>
          <Text code style={{ fontSize: 12 }}>{short(h.subject_id)}</Text>
        </Space>
      ),
    },
    {
      title: 'Employee',
      dataIndex: 'employee_id',
      key: 'employee_id',
      render: (v?: string | null) => (v ? <Text code>{short(v)}</Text> : '—'),
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'Scope',
      dataIndex: 'scope_services',
      key: 'scope_services',
      render: (svcs: string[]) => (
        <Space size={[0, 4]} wrap>
          {svcs.length
            ? svcs.map((s) => <Tag key={s} style={{ fontSize: 11 }}>{s.replace('-service', '')}</Tag>)
            : <Text type="secondary">—</Text>}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_: unknown, h: LegalHold) =>
        h.status === 'ACTIVE'
          ? <LegalHoldBadge active scope={h.scope_services} />
          : <Tag color="default">Released</Tag>,
    },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (v: string) => fmtDate(v) },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, h: LegalHold) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(h)}>View</Button>
          {canRelease && h.status === 'ACTIVE' && (
            <Tooltip title="Release this hold (reason required)">
              <Button size="small" danger icon={<UnlockOutlined />} onClick={() => setReleaseTarget(h)}>
                Release
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (!canView) {
    return (
      <Card>
        <Empty description="You do not have permission to view legal holds." />
      </Card>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          <SafetyCertificateOutlined /> Legal Holds
        </Title>
        <Text type="secondary">
          Legal holds block erasure, anonymization, and retention enforcement.
        </Text>
      </div>

      <DryRunBanner message="Legal holds override all retention, erasure, and anonymization — nothing under an active hold can be modified." />

      <DocumentHoldsCard canCreate={canCreate} canRelease={canRelease} />

      <Card
        style={{ marginTop: 16 }}
        title={
          <Space size={16} wrap>
            <Select<'ACTIVE' | 'RELEASED' | 'all'>
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'RELEASED', label: 'Released' },
              ]}
            />
            <Input.Search
              allowClear
              placeholder="Search tenant / employee / reason"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
            />
            <Select
              allowClear
              placeholder="Scope service"
              value={scopeFilter}
              onChange={setScopeFilter}
              style={{ width: 200 }}
              options={GOVERNANCE_SERVICES.map((s) => ({ value: s, label: s }))}
            />
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                Place legal hold
              </Button>
            )}
          </Space>
        }
      >
        <Table<LegalHold>
          rowKey="id"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No legal holds. Backend endpoint pending — this page is ready for when holds are created."
              />
            ),
          }}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
          Every create and release is recorded as audit evidence.
        </Text>
      </Card>

      {/* Create dialog */}
      <Modal
        title={<><PlusOutlined /> Place legal hold</>}
        open={createOpen}
        onOk={submitCreate}
        okText="Place hold"
        confirmLoading={creating}
        onCancel={() => setCreateOpen(false)}
        destroyOnClose
      >
        <Paragraph type="warning" style={{ fontSize: 13 }}>
          An active hold overrides all retention, erasure, and anonymization for its subject.
          This action is recorded as audit evidence.
        </Paragraph>
        <Form form={form} layout="vertical" requiredMark preserve={false}>
          <Form.Item name="tenant_id" label="Tenant ID" rules={[{ required: true, message: 'Tenant ID is required' }]}>
            <Input placeholder="tenant UUID" />
          </Form.Item>
          <Form.Item name="subject_type" label="Subject type" rules={[{ required: true, message: 'Subject type is required' }]}>
            <Select options={SUBJECT_TYPES.map((t) => ({ value: t, label: t }))} placeholder="Select subject type" />
          </Form.Item>
          <Form.Item name="subject_id" label="Subject ID" rules={[{ required: true, message: 'Subject ID is required' }]}>
            <Input placeholder="ID of the tenant, employee, record, or case" />
          </Form.Item>
          <Form.Item name="employee_id" label="Employee ID (optional)">
            <Input placeholder="employee UUID, if this hold targets a person" />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'A reason is required' }]}>
            <Input placeholder="e.g. Litigation hold — case #2026-014" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Additional context (optional)" />
          </Form.Item>
          <Form.Item name="scope_services" label="Scope (services)">
            <Select
              mode="multiple"
              allowClear
              placeholder="Which services this hold applies to"
              options={GOVERNANCE_SERVICES.map((s) => ({ value: s, label: s }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail view */}
      <Modal
        title={<><EyeOutlined /> Legal hold detail</>}
        open={!!detail}
        footer={<Button onClick={() => setDetail(null)}>Close</Button>}
        onCancel={() => setDetail(null)}
        width={640}
        destroyOnClose
      >
        {detail && (
          <>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status">
                {detail.status === 'ACTIVE'
                  ? <LegalHoldBadge active scope={detail.scope_services} />
                  : <Tag>Released</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Tenant">{detail.tenant_id}</Descriptions.Item>
              <Descriptions.Item label="Subject">
                <Tag color={subjectColor(detail.subject_type)}>{detail.subject_type}</Tag> {detail.subject_id}
              </Descriptions.Item>
              <Descriptions.Item label="Employee">{detail.employee_id || '—'}</Descriptions.Item>
              <Descriptions.Item label="Reason">{detail.reason}</Descriptions.Item>
              {detail.description && (
                <Descriptions.Item label="Description">{detail.description}</Descriptions.Item>
              )}
              <Descriptions.Item label="Scope">
                <Space size={[0, 4]} wrap>
                  {detail.scope_services.length
                    ? detail.scope_services.map((s) => <Tag key={s}>{s}</Tag>)
                    : <Text type="secondary">All services</Text>}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {fmtDate(detail.created_at)}{detail.created_by ? ` · ${detail.created_by}` : ''}
              </Descriptions.Item>
              {detail.status === 'RELEASED' && (
                <Descriptions.Item label="Released">
                  {fmtDate(detail.released_at)}{detail.released_by ? ` · ${detail.released_by}` : ''}
                  {detail.release_reason ? ` — ${detail.release_reason}` : ''}
                </Descriptions.Item>
              )}
            </Descriptions>
            <Title level={5}>Timeline</Title>
            <ComplianceTimeline events={detailEvents(detail)} />
          </>
        )}
      </Modal>

      {/* Release confirmation */}
      <ConfirmDangerDialog
        open={!!releaseTarget}
        title="Release legal hold"
        description={
          releaseTarget
            ? `Releasing this hold lifts protection on ${releaseTarget.subject_type.toLowerCase()} ${short(releaseTarget.subject_id)}. Retention, erasure, and anonymization may resume for its scope.`
            : ''
        }
        warning="This is recorded as audit evidence. A reason is required."
        confirmLabel="Release hold"
        requireReason
        loading={releasing}
        onConfirm={confirmRelease}
        onCancel={() => setReleaseTarget(null)}
      />
    </div>
  );
};

export default LegalHoldsPage;

// Document holds (kp-platform §10) — REAL execution: fans a subject-wide hold
// (or release) out to employee/credit/payroll/benefits retention executors and
// shows what each service did. Held documents survive erasure by construction.
const DOC_HOLD_REASONS = [
  'legal_hold', 'audit', 'investigation', 'outstanding_loan',
  'payroll_retention', 'partner_requirement', 'tax_requirement',
];

const DocumentHoldsCard: React.FC<{ canCreate: boolean; canRelease: boolean }> = ({ canCreate, canRelease }) => {
  const [form] = Form.useForm<{ tenant_id: string; user_id: string; action: 'apply' | 'release'; reason?: string }>();
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<Record<string, DocumentHoldResult> | null>(null);
  const action = Form.useWatch('action', form);

  const run = async () => {
    const values = await form.validateFields();
    setRunning(true);
    setResults(null);
    try {
      const res = await executeDocumentHold(values);
      setResults(res);
      const total = Object.values(res).reduce((n, r) => n + (r.updated || 0), 0);
      message.success(`${values.action === 'apply' ? 'Hold applied to' : 'Hold released on'} ${total} document${total === 1 ? '' : 's'} across services.`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Document hold failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card
      style={{ marginTop: 16 }}
      title={<Space><SafetyCertificateOutlined /> Document holds (live execution)</Space>}
      extra={<Text type="secondary">Fans out to every service&apos;s document retention executor</Text>}
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Applies (or releases) a preservation hold on <b>every document a subject holds across
        employee, credit, payroll and benefits</b>. While held, documents cannot be deleted —
        including by DSAR erasure. Results below show what each service actually did.
      </Paragraph>
      <Form form={form} layout="inline" initialValues={{ action: 'apply', reason: 'legal_hold' }}>
        <Form.Item name="tenant_id" rules={[{ required: true, message: 'Tenant ID required' }]}>
          <Input placeholder="Tenant ID (uuid)" style={{ width: 300 }} />
        </Form.Item>
        <Form.Item name="user_id" rules={[{ required: true, message: 'Subject user ID required' }]}>
          <Input placeholder="Subject user ID (uuid)" style={{ width: 300 }} />
        </Form.Item>
        <Form.Item name="action" rules={[{ required: true }]}>
          <Select style={{ width: 130 }} options={[
            { value: 'apply', label: 'Apply hold', disabled: !canCreate },
            { value: 'release', label: 'Release hold', disabled: !canRelease },
          ]} />
        </Form.Item>
        {action !== 'release' && (
          <Form.Item name="reason" rules={[{ required: true }]}>
            <Select style={{ width: 190 }} options={DOC_HOLD_REASONS.map((r) => ({ value: r, label: r.replace(/_/g, ' ') }))} />
          </Form.Item>
        )}
        <Form.Item>
          <Button type="primary" loading={running} onClick={run} disabled={!canCreate && !canRelease}>
            Execute
          </Button>
        </Form.Item>
      </Form>
      {results && (
        <Table
          size="small"
          style={{ marginTop: 16 }}
          pagination={false}
          rowKey={(r: any) => r.service}
          dataSource={Object.entries(results).map(([service, r]) => ({ service, ...r }))}
          columns={[
            { title: 'Service', dataIndex: 'service' },
            { title: 'Documents updated', dataIndex: 'updated' },
            { title: 'Skipped', dataIndex: 'skipped' },
            {
              title: 'Status', key: 'status',
              render: (_: any, r: any) => r.error
                ? <Tag color="red">{r.error}</Tag>
                : <Tag color="green">ok</Tag>,
            },
          ]}
        />
      )}
    </Card>
  );
};
